import { z } from "zod";

import { memberPhoneSchema } from "./phone.ts";
import { pageQuerySchema } from "./common.ts";

export const productStatusSchema = z.enum(["draft", "pending", "active", "rejected", "offline"]);

/** 前台商品卡片。⚠️ `fromPrice` 是按访问者归属链解析出来的，不是库里的某个字段。 */
export const productCardSchema = z.object({
  id: z.number().int(),
  slug: z.string(),
  title: z.string(),
  subtitle: z.string(),
  brandId: z.number().int().nullable(),
  categoryId: z.number().int(),
  spaces: z.array(z.string()),
  imageUrls: z.array(z.string()),
  fromPrice: z.number().int(),
  salesCount: z.number().int(),
});

export const buyerSkuSchema = z.object({
  id: z.number().int(),
  specs: z.record(z.string(), z.string()),
  price: z.number().int(),
  listPrice: z.number().int().nullable(),
  stock: z.number().int(),
  unit: z.string(),
  imageUrl: z.string().nullable(),
  available: z.boolean(),
});

export const productDetailSchema = productCardSchema.extend({
  attributes: z.record(z.string(), z.string()),
  descMd: z.string(),
  installNote: z.string(),
  /**
   * 详情图 —— 详情页最下面那一长条。
   *
   * 🔴 **跟卡片上的 `imageUrls` 是两回事，别合并**（淘宝那套）：
   *    `imageUrls` 是"商品长什么样"（列表卡、轮播、购物车和订单缩略图都取它，
   *    要方图）；这一组是"介绍商品"（材质说明、施工示意、尺寸表），
   *    通常是 800×3000 这种竖长图，只出现在详情页最下面。
   *    合成一个数组的话，那几张长图会缩成 80×80 的方块跑进列表和订单里。
   */
  detailImageUrls: z.array(z.string()),
  skus: z.array(buyerSkuSchema),
});

export type ProductCard = z.infer<typeof productCardSchema>;
export type BuyerSku = z.infer<typeof buyerSkuSchema>;
export type ProductDetail = z.infer<typeof productDetailSchema>;

export const productListQuerySchema = pageQuerySchema.extend({
  categoryId: z.coerce.number().int().optional(),
  brandId: z.coerce.number().int().optional(),
  space: z.string().max(20).optional(),
  keyword: z.string().max(60).optional(),
  sort: z.enum(["newest", "sales", "price_asc", "price_desc"]).default("newest"),
});
export type ProductListQuery = z.infer<typeof productListQuerySchema>;

// ── 供应商侧 ──

export const createProductSchema = z.object({
  categoryId: z.number().int().positive(),
  title: z.string().min(2).max(120),
  subtitle: z.string().max(120).optional(),
  brandId: z.number().int().positive().optional(),
  spaces: z.array(z.string()).max(10).optional(),
  style: z.string().max(30).optional(),
  imageKeys: z.array(z.string()).max(20).optional(),
  /**
   * 详情图 —— 详情页最下面那一长条（淘宝那套：商品图 vs 商品详情）。
   * ⚠️ 跟 `imageKeys` 分开：那组是"商品长什么样"（列表卡、订单缩略图都取它），
   *    这组是"介绍商品"，通常是几张竖着排的长图，只出现在详情页。
   */
  detailImageKeys: z.array(z.string()).max(30).optional(),
  attributes: z.record(z.string(), z.string()).optional(),
  descMd: z.string().max(20_000).optional(),
  installNote: z.string().max(5_000).optional(),
  freightMode: z.enum(["weight", "volume", "piece", "free"]).optional(),
  freightTemplateId: z.number().int().positive().optional(),
});
export type CreateProductInput = z.infer<typeof createProductSchema>;

export const createSkuSchema = z.object({
  productId: z.number().int().positive(),
  specs: z.record(z.string(), z.string()),
  /** ⚠️ 供货价，单位分。这是链条的起点，对下级永远不可见。 */
  supplyPrice: z.number().int().nonnegative(),
  listPrice: z.number().int().nonnegative().optional(),
  stock: z.number().int().nonnegative().optional(),
  unit: z.string().max(10).optional(),
  unitsPerPack: z.number().positive().optional(),
  weightGram: z.number().int().nonnegative().optional(),
  volumeMl: z.number().int().nonnegative().optional(),
  imageKey: z.string().optional(),
  skuCode: z.string().max(50).optional(),
});
export type CreateSkuInput = z.infer<typeof createSkuSchema>;

/**
 * 平台价（指导零售价 / 吊牌价）。
 * ⚠️ `null` = 取消平台价，跟"没传"是两回事，所以是 nullable 不是 optional。
 */
/**
 * 审核通过时一并定下的**平台价**。
 * 🔴 老板：「审核时就会有平台价，审核通过才会上架平台」——
 *    所以它不是可选的补充信息，是上架的前置条件。
 * ⚠️ key 是 skuId（JSON 的 key 只能是字符串，服务端会转回数字）。
 */
export const approveProductSchema = z.object({
  platformPrices: z.record(z.string(), z.number().int().positive()).optional(),
  note: z.string().max(500).optional(),
});

export const setPlatformPriceSchema = z.object({
  listPrice: z.number().int().positive().nullable(),
});

export const adjustStockSchema = z.object({
  /** ⚠️ 相对增减，不是设值 —— 两人同时补货时"设成 100"会互相覆盖。 */
  delta: z.number().int(),
});

export const rejectProductSchema = z.object({
  reason: z.string().min(1, "驳回必须填写理由").max(500),
});

// ─────────────────────────── 品类 / 品牌 / 运费模板 ───────────────────────────

/**
 * 🔴 **品类是整个前台浏览的骨架**（首页、分类页、商品筛选、店铺的自动货架）。
 *    以前后台没有任何接口，改一个名字只能连数据库改。
 *
 * ⚠️ `slug` 留空由服务端按名字生成 —— 运营不知道 slug 是什么，也不该逼他懂。
 */
export const categoryInputSchema = z.object({
  name: z.string().min(1, "品类名不能为空").max(120),
  slug: z.string().max(80).optional(),
  parentId: z.number().int().positive().nullable().optional(),
  /** 适用空间（客厅/卧室/厨房…），一个品类可跨多个空间。 */
  spaces: z.array(z.string().max(20)).max(20).optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

/**
 * ⚠️ PATCH 用**显式全 optional 且不带 default** 的 schema。
 *    拿 `.partial()` 当入参校验的话，`.default()` 不会被去掉 ——
 *    只传一个字段，解析出来却带着所有默认值，它们会把库里的值覆盖掉。
 *    抽成策略就这么中过招。
 */
export const patchCategorySchema = z.object({
  name: z.string().min(1).max(120).optional(),
  slug: z.string().max(80).optional(),
  parentId: z.number().int().positive().nullable().optional(),
  spaces: z.array(z.string().max(20)).max(20).optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

export const brandInputSchema = z.object({
  name: z.string().min(1, "品牌名不能为空").max(120),
  slug: z.string().max(80).optional(),
  description: z.string().max(500).optional(),
  logoKey: z.string().max(255).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

export const patchBrandSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  description: z.string().max(500).optional(),
  logoKey: z.string().max(255).nullable().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
  active: z.boolean().optional(),
});

/**
 * 运费模板。
 *
 * ⚠️ `volume` 模式要先把体积换算成体积重再跟实重比 —— 额度对重量的单位是克、
 *    对体积是毫升，直接比没有意义。换算在 `pricing` 那边做，这里只存配置。
 */
export const freightConfigSchema = z.object({
  firstQuota: z.number().int().nonnegative().optional(),
  firstFeeCents: z.number().int().nonnegative().optional(),
  stepQuota: z.number().int().positive().optional(),
  stepFeeCents: z.number().int().nonnegative().optional(),
  /** 满这个金额包邮。⚠️ 0 表示不包邮，不是"0 元就包邮"。 */
  freeThresholdCents: z.number().int().nonnegative().optional(),
});

export const freightInputSchema = z.object({
  name: z.string().min(1, "模板名不能为空").max(120),
  orgId: z.number().int().positive().nullable().optional(),
  mode: z.enum(["weight", "volume", "piece"]).optional(),
  config: freightConfigSchema.optional(),
});

export const patchFreightSchema = z.object({
  name: z.string().min(1).max(120).optional(),
  mode: z.enum(["weight", "volume", "piece"]).optional(),
  config: freightConfigSchema.optional(),
});

// ─────────────────────────── 平台级价格规则与限价 ───────────────────────────

/**
 * 🔴 **价格规则决定"没单独定价时按什么算"。**
 *    `scope` 选谁：全平台 / 某类身份（经销商、分销商）/ 某个等级 / 某个组织。
 *
 * ⚠️ `value` 按 `mode` 解释：`markup_rate` 是**小数**（0.15 = 加 15%），
 *    另外两个是**整数分**。混着填会让价格差一百倍。
 */
export const priceRuleSchema = z.object({
  scope: z.enum(["platform", "org_type", "level", "org"]),
  scopeValue: z.string().max(60).optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  productId: z.number().int().positive().nullable().optional(),
  mode: z.enum(["markup_rate", "markup_fixed", "absolute"]),
  value: z.number(),
  note: z.string().max(500).optional(),
  active: z.boolean().optional(),
});

export const patchPriceRuleSchema = z.object({
  scope: z.enum(["platform", "org_type", "level", "org"]).optional(),
  scopeValue: z.string().max(60).optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  productId: z.number().int().positive().nullable().optional(),
  mode: z.enum(["markup_rate", "markup_fixed", "absolute"]).optional(),
  value: z.number().optional(),
  note: z.string().max(500).optional(),
  active: z.boolean().optional(),
});

/**
 * 🔴 **限价决定商家能把价压到多低。**
 *    `minMarkupRate = 0` 就是不得低于进货价；`belowCostPolicy` 决定
 *    亏本卖是放行 / 提示 / 拦死。
 * ⚠️ `warn` 是默认：有时候确实要冲量，一刀切拦死会挡掉正常生意。
 */
export const priceLimitSchema = z.object({
  scope: z.enum(["platform", "org_type", "level", "org"]),
  scopeValue: z.string().max(60).optional(),
  categoryId: z.number().int().positive().nullable().optional(),
  productId: z.number().int().positive().nullable().optional(),
  minMarkupRate: z.number().nullable().optional(),
  maxMarkupRate: z.number().nullable().optional(),
  belowCostPolicy: z.enum(["allow", "warn", "block"]).optional(),
  note: z.string().max(500).optional(),
  active: z.boolean().optional(),
});

export const patchPriceLimitSchema = priceLimitSchema.extend({
  scope: z.enum(["platform", "org_type", "level", "org"]).optional(),
});

// ─────────────────────────── 用户 / 公告 / 运维 ───────────────────────────

/** ⚠️ 禁用必须写理由：既是留痕，也是本人在站内信里看得到的解释。 */
export const setUserDisabledSchema = z.object({
  disabled: z.boolean(),
  reason: z.string().min(1, "必须写理由").max(500),
});

/**
 * 后台代建一个会员账号。
 *
 * 🔴 **手机号必填** —— 这个站是手机号登录的，没有手机号的账号建出来他登不上。
 * ⚠️ 后台代建**不走人机验证和短信码**（那两道是拦机器批量注册的，
 *    而这里的操作者是已经登录的后台管理员，已经过了一道更严的门）。
 */
export const adminCreateUserSchema = z.object({
  /** ⚠️ 跟前台注册同一套规则（多地区 + E.164），后台代开的号才登得进去。 */
  phone: memberPhoneSchema,
  password: z.string().min(8, "密码至少 8 位").max(64),
  nickname: z.string().trim().max(30).optional(),
  email: z.string().trim().email("邮箱格式不对").max(120).optional().or(z.literal("")),
  /** 归到哪个商家名下（专属服务商）。不填 = 无归属，他按平台指导零售价买。 */
  bindOrgId: z.number().int().positive().optional(),
  reason: z.string().max(500).optional(),
});

/**
 * 改一个会员的资料。
 *
 * ⚠️ **全 optional 且不带 default** —— 拿 `.partial()` 当 PATCH 入参会把
 *    default 字段一起解析出来，然后覆盖掉库里的值（抽成策略中过这一招）。
 * 🔴 传了 `password` 就是**重置密码**，会把他所有设备踢下线。
 */
export const adminUpdateUserSchema = z.object({
  phone: memberPhoneSchema.optional(),
  email: z.string().trim().max(120).optional(),
  nickname: z.string().trim().max(30).optional(),
  password: z.string().min(8, "密码至少 8 位").max(64).optional(),
  reason: z.string().max(500).optional(),
});

/**
 * 删除一个会员账号。
 *
 * 🔴 **删除和禁用是两件事，都要有**（跟后台账号那两个按钮同一个道理）：
 *    禁用 = 暂时进不来，记录还在、随时能恢复、历史订单仍然对得上人；
 *    删除 = 这个账号本来就不该存在（测试号、重复注册、刷号）。
 *    只给禁用的话，一堆垃圾账号永远躺在列表里，谁也不敢清。
 * ⚠️ 必须写理由：删完就查不回来了，这条理由是唯一的痕迹。
 */
export const adminDeleteUserSchema = z.object({
  reason: z.string().min(1, "删除必须写理由").max(500),
});

/**
 * 给一个会员挂上商家身份。
 *
 * 🔴 **`parentOrgId` 是价格链的起点，不是分类标签** —— 挂在谁下面决定了
 *    他的进货价、分佣给谁、客户属于哪一支。所以必填，不给"默认挂平台"。
 * ⚠️ 平台节点不给建：全站只有一个，它是链条的根。
 */
export const grantIdentitySchema = z.object({
  type: z.enum(["supplier", "dealer", "distributor"]),
  parentOrgId: z.number().int().positive(),
  name: z.string().trim().min(2, "店铺/公司名至少 2 个字").max(120),
  contactPhone: z.string().trim().max(20).optional(),
  reason: z.string().max(500).optional(),
});

/** ⚠️ 公告发出去撤不回来，所以标题正文都当场定稿。 */
export const broadcastSchema = z.object({
  audience: z.enum(["all", "merchants", "org"]),
  orgId: z.number().int().positive().optional(),
  title: z.string().min(1, "标题不能为空").max(120),
  body: z.string().max(1000).optional(),
  link: z.string().max(255).optional(),
});

export const resolveErrorsSchema = z.object({
  ids: z.array(z.number().int().positive()).min(1).max(200),
});
